/**
 * EventsTable — virtualized risk-events grid for Investigations.
 *
 * Uses TanStack Table v8 for column model + sorting + visibility, and
 * @tanstack/react-virtual for row recycling so 50+ rows stay smooth.
 * Clickable mono cells (IP / ASN / JA4 / email / business / session)
 * open the EntityDetailDrawer; non-mono cells show readable badges.
 */
import { useMemo, useRef, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import clsx from "clsx";
import type { RiskEventV1, EntityTypeV1 } from "@/types/v1";
import { RiskBandBadge, BotScoreBadge } from "@/components/badges";
import { MethodBadge } from "@/components/badges/MethodBadge";
import { MonoValue } from "@/components/primitives/MonoValue";
import { ReasonChips } from "@/components/reasons/ReasonChips";
import { IdentityCell } from "@/components/identity/IdentityCell";
import { parseReasons } from "@/lib/reasonCodes";
import { formatTime, formatRelative } from "@/lib/time";
import { fmtNumber, truncateMiddle } from "@/lib/format";
import { countryName } from "@/config/geo";
import { endpointAreaLabel } from "@/config/filters";

interface Props {
  rows: RiskEventV1[];
  sort: { id: string; desc: boolean };
  onSortChange: (s: { id: string; desc: boolean }) => void;
  onEntity: (type: EntityTypeV1, value: string) => void;
}

export function EventsTable({ rows, sort, onSortChange, onEntity }: Props) {
  const [sorting, setSorting] = useState<SortingState>([{ id: sort.id, desc: sort.desc }]);

  const columns = useMemo<ColumnDef<RiskEventV1>[]>(() => [
    { id: "ts", header: "Time", accessorKey: "ts", cell: ({ row }) => (
        <span title={formatTime(row.original.ts)} className="whitespace-nowrap text-fg-muted">{formatRelative(row.original.ts)}</span>
      ), size: 88 },
    { id: "score", header: "Risk", accessorKey: "score", cell: ({ row }) => (
        <div className="flex min-w-max items-center gap-2">
          <span className="font-mono text-fg">{row.original.score}</span>
          <RiskBandBadge band={row.original.band} />
        </div>
      ), size: 132 },
    { id: "endpoint", header: "Area", enableSorting: false, cell: ({ row }) => {
        const r = row.original;
        const path = r.pathBucket || "\u2014";
        const display = path.length > 36 ? truncateMiddle(path, 36) : path;
        const detail = r.endpointType ? `${r.endpointType} · ${display}` : display;
        return (
          <div className="min-w-0 leading-tight">
            <button
              onClick={() => onEntity("endpointGroup", r.endpointGroup)}
              className="text-sm font-medium text-fg hover:text-accent"
              title={`Filter to ${endpointAreaLabel(r.endpointGroup)}`}
            >{endpointAreaLabel(r.endpointGroup)}</button>
            <div className="mt-1 flex min-w-0 items-center gap-1.5 text-[11px] text-fg-subtle">
              <MethodBadge method={r.method} />
              <span className="truncate font-mono" title={path}>{detail}</span>
            </div>
          </div>
        );
      }, size: 240 },
    { id: "email", header: "Email", accessorKey: "email", cell: ({ row }) => (
        row.original.email
          ? <MonoValue value={truncateMiddle(row.original.email, 20)} onClick={() => onEntity("email", row.original.email!)} />
          : <IdentityCell row={row.original} onEntity={onEntity} />
      ), size: 180 },
    { id: "businessId", header: "Business", accessorKey: "businessId", cell: ({ row }) => (
        row.original.businessId
          ? <MonoValue value={row.original.businessId} onClick={() => onEntity("business", row.original.businessId!)} />
          : <span className="text-fg-subtle">—</span>
      ), size: 130 },
    { id: "ip", header: "IP", accessorKey: "ip", cell: ({ row }) => (
        <MonoValue value={row.original.ip} onClick={() => onEntity("ip", row.original.ip)} />
      ), size: 130 },
    { id: "country", header: "Country", accessorKey: "country", cell: ({ row }) => (
        row.original.country ? (
          <button onClick={() => onEntity("country", row.original.country!)} className="text-fg-muted hover:text-fg" title={countryName(row.original.country)}>
            {row.original.country}
          </button>
        ) : <span className="text-fg-subtle">—</span>
      ), size: 76 },
    { id: "asn", header: "ASN", accessorKey: "asn", cell: ({ row }) => (
        row.original.asn ? <MonoValue value={`AS${row.original.asn}`} onClick={() => onEntity("asn", String(row.original.asn))} /> : <span className="text-fg-subtle">—</span>
      ), size: 86 },
    { id: "ja4", header: "JA4 fingerprint", accessorKey: "ja4", cell: ({ row }) => (
        row.original.ja4 ? <MonoValue value={truncateMiddle(row.original.ja4, 18)} onClick={() => onEntity("ja4", row.original.ja4!)} /> : <span className="text-fg-subtle">—</span>
      ), size: 170 },
    { id: "botScore", header: "Bot Score", accessorKey: "botScore", cell: ({ row }) => <BotScoreBadge score={row.original.botScore} />, size: 76 },
    { id: "reasons", header: "Reasons", accessorKey: "reasons", enableSorting: false, cell: ({ row }) => (
        <ReasonChips
          parsed={parseReasons(row.original.reasons)}
          contributions={row.original.categoryContributions}
          top={3}
        />
      ), size: 280 },
  ], [onEntity]);

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting },
    onSortingChange: (updater) => {
      const next = typeof updater === "function" ? updater(sorting) : updater;
      setSorting(next);
      const head = next[0];
      if (head) onSortChange({ id: head.id, desc: !!head.desc });
    },
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: table.getRowModel().rows.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => 58,
    overscan: 8,
  });

  return (
    <div ref={containerRef} className="max-h-[70vh] overflow-auto rounded-lg border border-border bg-surface-1">
      <table className="text-sm" style={{ tableLayout: "fixed", minWidth: "100%", width: table.getTotalSize() }}>
        <thead className="sticky top-0 z-10 bg-surface-3 text-[11px] uppercase tracking-wider text-fg-subtle">
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((h) => (
                <th
                  key={h.id}
                  style={{ width: h.getSize() }}
                  className={clsx(
                    "border-b border-border px-3 py-2 text-left font-medium",
                    h.column.getCanSort() && "cursor-pointer select-none hover:text-fg",
                  )}
                  onClick={h.column.getCanSort() ? h.column.getToggleSortingHandler() : undefined}
                >
                  {flexRender(h.column.columnDef.header, h.getContext())}
                  {h.column.getIsSorted() === "asc" && " ↑"}
                  {h.column.getIsSorted() === "desc" && " ↓"}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody style={{ height: rowVirtualizer.getTotalSize(), position: "relative", display: "block" }}>
          {rowVirtualizer.getVirtualItems().map((vRow) => {
            const row = table.getRowModel().rows[vRow.index];
            return (
              <tr
                key={row.id}
                style={{
                  position: "absolute", top: 0, left: 0, width: "100%",
                  transform: `translateY(${vRow.start}px)`, height: vRow.size, display: "table", tableLayout: "fixed",
                }}
                className="border-b border-border hover:bg-surface-2"
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    style={{ width: cell.column.getSize() }}
                    className={clsx(
                      "overflow-hidden px-3 py-2 align-middle",
                      cell.column.id === "endpoint" ? "whitespace-normal" : "whitespace-nowrap",
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      {rows.length === 0 && (
        <div className="p-8 text-center text-sm text-fg-subtle">No events match the current filters.</div>
      )}
      <div className="border-t border-border bg-surface-2 px-3 py-2 text-xs text-fg-subtle">
        Showing {fmtNumber(rows.length)} rows · click a mono cell to inspect that entity
      </div>
    </div>
  );
}
